import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Typography,
  Tooltip,
} from "@material-ui/core";
import { useParams, useHistory, useLocation } from "react-router-dom";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { makeStyles } from "@material-ui/core/styles";
import InfoIcon from "@material-ui/icons/Info";
import { useAppContext } from "../../api/AppContext";
import { useSnackbar } from "notistack";

const useStyles = makeStyles({
  "@keyframes shake": {
    "0%, 100%": { transform: "translateX(0)" },
    "10%, 30%, 50%, 70%, 90%": { transform: "translateX(-4px)" },
    "20%, 40%, 60%, 80%": { transform: "translateX(4px)" },
  },
  shakeAnimation: {
    animation: "$shake 0.5s ease-in-out",
  },
});

const EmployeeEditPage = () => {
  const classes = useStyles();
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  const tenantFromQuery = React.useMemo(() => {
    try {
      const sp = new URLSearchParams(location.search || "");
      return (
        sp.get("tenantid") || sp.get("tenantId") || sp.get("companyId") || ""
      );
    } catch (_e) {
      return "";
    }
  }, [location.search]);

  const {
    employeeRegistryEmployees,
    isEmployeeRegistryEmployeesLoading,
    fetchEmployeeRegistryEmployees,
    updateEmployeeRegistryEmployee,
    employeeRegistryEntityValues,
    employeeRegistryContractTypeValues,
    employeeRegistryEmployeeTypeValues,
    employeeRegistryEmployeeRoleValues,
    employeeRegistryOfficeLocationValues,
    isEmployeeRegistryValuesLoading,
    fetchEmployeeRegistryValues,
  } = useAppContext();

  const [employee, setEmployee] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [shouldShake, setShouldShake] = useState(false);
  const previousEntityRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      try {
        setIsLoading(true);
        await fetchEmployeeRegistryValues();

        let empRow = (employeeRegistryEmployees || []).find(
          (r) => String(r.id) === String(id)
        );

        if (!empRow) {
          const rows = await fetchEmployeeRegistryEmployees(
            tenantFromQuery || "all"
          );
          empRow = (rows || []).find((r) => String(r.id) === String(id));
        }

        const mapped = empRow
          ? {
              id: empRow.id || "",
              givenname: empRow.givenname || "",
              surname: empRow.surname || "",
              mail: empRow.mail || "",

              isdeleted: empRow.isdeleted ?? false,
              accountenabled: empRow.accountenabled ?? true,

              created_at: empRow.created_at || "",
              refreshed_at: empRow.refreshed_at || "",
              updated_at: empRow.updated_at || "",
              updated_by: empRow.updated_by || "",

              usertype: empRow.usertype || "",
              hasWecareEnabled: empRow.haswecareenabled ?? false,

              entity: empRow.entity,
              contracttype: empRow.contracttype,
              employeetype: empRow.employeetype,
              employeerole: empRow.employeerole,
              officelocation: empRow.officelocation,
              officetype: empRow.officetype,

              isfromtenant: empRow.isfromtenant || tenantFromQuery || "",
            }
          : null;

        if (!mounted) return;
        setEmployee(mapped);
      } catch (e) {
        if (!mounted) return;
        const msg = e?.message || "Failed to load data";
        enqueueSnackbar(msg, { variant: "error" });
      } finally {
        if (mounted) setIsLoading(false);
      }
    };
    run();
    return () => {
      mounted = false;
    };
  }, [
    id,
    fetchEmployeeRegistryValues,
    employeeRegistryEmployees,
    fetchEmployeeRegistryEmployees,
    tenantFromQuery,
  ]);

  const handleChange = (field) => (e, newValue) => {
    const value = newValue?.value ?? newValue ?? "";
    setEmployee((prev) => {
      // Triggering shake animation on entity value change
      if (field === "entity" && prev) {
        const previousValue = previousEntityRef.current ?? prev.entity;
        if (previousValue !== value && value !== "") {
          setShouldShake(true);
          setTimeout(() => setShouldShake(false), 500);
        }
        // Updating ref for next change
        previousEntityRef.current = value;
      }
      return { ...prev, [field]: value };
    });
  };

  const getOptionFromValue = (options, value) => {
    if (!value || !options) return null;
    return options.find((opt) => opt.value === value) || null;
  };

  // Tracking initial entity value
  useEffect(() => {
    if (
      employee &&
      employee.entity !== undefined &&
      previousEntityRef.current === null
    ) {
      previousEntityRef.current = employee.entity;
    }
  }, [employee]);

  const handleSave = async () => {
    try {
      setIsSaving(true);

      const payload = {
        id: employee.id,
        isfromtenant: employee.isfromtenant || tenantFromQuery,
        entity: employee.entity,
        contracttype: employee.contracttype,
        employeetype: employee.employeetype,
        employeerole: employee.employeerole,
        officelocation: employee.officelocation,
        officetype: employee.officetype,
      };

      await updateEmployeeRegistryEmployee(payload);
      history.push("/welfares/employee-registry");
    } catch (e) {
      const msg = e?.message || "Failed to save";
      enqueueSnackbar(msg, { variant: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading || isEmployeeRegistryEmployeesLoading) {
    return (
      <Box
        alignItems="center"
        display="flex"
        justifyContent="center"
        width="100%"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!employee) {
    return (
      <Box p={4}>
        <Typography color="error">Employee not found</Typography>
      </Box>
    );
  }

  return (
    <Box p={4}>
      <Box mb={2}>
        <Button
          disabled={isSaving}
          variant="outlined"
          onClick={() => history.push("/welfares/employee-registry")}
        >
          Cancel
        </Button>
      </Box>
      <Grid container spacing={3}>
        {/* Using snackbar for error messages as in other pages */}

        <Grid item md={6} xs={12}>
          <TextField
            disabled
            fullWidth
            label="ID"
            value={employee.id}
            variant="outlined"
          />
        </Grid>

        <Grid item md={6} xs={12}>
          <TextField
            disabled
            fullWidth
            label="Given Name"
            value={employee.givenname}
            variant="outlined"
          />
        </Grid>

        <Grid item md={6} xs={12}>
          <TextField
            disabled
            fullWidth
            label="Surname"
            value={employee.surname}
            variant="outlined"
          />
        </Grid>

        <Grid item md={6} xs={12}>
          <TextField
            disabled
            fullWidth
            label="Mail"
            value={employee.mail}
            variant="outlined"
          />
        </Grid>

        <Grid item md={6} xs={12}>
          <TextField
            disabled
            fullWidth
            label="Is Deleted"
            value={String(employee.isdeleted)}
            variant="outlined"
          />
        </Grid>

        <Grid item md={6} xs={12}>
          <TextField
            disabled
            fullWidth
            label="Account Enabled"
            value={String(employee.accountenabled)}
            variant="outlined"
          />
        </Grid>

        <Grid item md={6} xs={12}>
          <TextField
            disabled
            fullWidth
            label="Created At"
            value={employee.created_at}
            variant="outlined"
          />
        </Grid>

        <Grid item md={6} xs={12}>
          <TextField
            disabled
            fullWidth
            label="Refreshed At"
            value={employee.refreshed_at}
            variant="outlined"
          />
        </Grid>

        <Grid item md={6} xs={12}>
          <TextField
            disabled
            fullWidth
            label="User Type"
            value={employee.usertype}
            variant="outlined"
          />
        </Grid>

        <Grid item md={6} xs={12}>
          <TextField
            disabled
            fullWidth
            label="Has Wecare Installed"
            value={String(employee.hasWecareEnabled)}
            variant="outlined"
          />
        </Grid>

        <Grid item md={6} xs={12}>
          <TextField
            disabled
            fullWidth
            label="Updated At"
            value={employee.updated_at}
            variant="outlined"
          />
        </Grid>

        <Grid item md={6} xs={12}>
          <TextField
            disabled
            fullWidth
            label="Updated By"
            value={employee.updated_by}
            variant="outlined"
          />
        </Grid>

        <Grid item md={6} xs={12}>
          <Box marginBottom={1}>
            <Typography style={{ visibility: "hidden" }} variant="body2">
              Placeholder
            </Typography>
          </Box>
          <Autocomplete
            disabled={isEmployeeRegistryValuesLoading || isSaving}
            getOptionLabel={(option) =>
              option.label ?? String(option.value ?? "")
            }
            getOptionSelected={(option, value) => option.value === value.value}
            options={employeeRegistryEmployeeRoleValues || []}
            renderInput={(params) => (
              <TextField
                {...params}
                InputLabelProps={{ shrink: true }}
                label="Employee Role"
                variant="outlined"
              />
            )}
            value={getOptionFromValue(
              employeeRegistryEmployeeRoleValues,
              employee.employeerole
            )}
            onChange={handleChange("employeerole")}
          />
        </Grid>

        <Grid item md={6} xs={12}>
          <Box
            alignItems="center"
            display="flex"
            marginBottom={1}
            style={{ gap: 8 }}
          >
            <Typography variant="body2">Entity</Typography>
            <Tooltip
              placement="top-start"
              title={
                <Box p={2}>
                  <Typography variant="subtitle2">Entity Change</Typography>
                  <br />
                  <Typography variant="caption">
                    In case of entity change, the user will switch to the
                    related journey during next morning
                  </Typography>
                </Box>
              }
            >
              <Box
                alignItems="center"
                display="flex"
                style={{ cursor: "pointer" }}
              >
                <InfoIcon
                  className={shouldShake ? classes.shakeAnimation : ""}
                  color="action"
                  fontSize="small"
                />
              </Box>
            </Tooltip>
          </Box>
          <Autocomplete
            disabled={isEmployeeRegistryValuesLoading || isSaving}
            getOptionLabel={(option) =>
              option.label ?? String(option.value ?? "")
            }
            getOptionSelected={(option, value) => option.value === value.value}
            options={employeeRegistryEntityValues || []}
            renderInput={(params) => (
              <TextField
                {...params}
                InputLabelProps={{ shrink: true }}
                label="Entity"
                variant="outlined"
              />
            )}
            value={getOptionFromValue(
              employeeRegistryEntityValues,
              employee.entity
            )}
            onChange={handleChange("entity")}
          />
        </Grid>

        <Grid item md={6} xs={12}>
          <Autocomplete
            disabled={isEmployeeRegistryValuesLoading || isSaving}
            getOptionLabel={(option) =>
              option.label ?? String(option.value ?? "")
            }
            getOptionSelected={(option, value) => option.value === value.value}
            options={employeeRegistryEmployeeTypeValues || []}
            renderInput={(params) => (
              <TextField
                {...params}
                InputLabelProps={{ shrink: true }}
                label="Employee Type"
                variant="outlined"
              />
            )}
            value={getOptionFromValue(
              employeeRegistryEmployeeTypeValues,
              employee.employeetype
            )}
            onChange={handleChange("employeetype")}
          />
        </Grid>

        <Grid item md={6} xs={12}>
          <Autocomplete
            disabled={isEmployeeRegistryValuesLoading || isSaving}
            getOptionLabel={(option) =>
              option.label ?? String(option.value ?? "")
            }
            getOptionSelected={(option, value) => option.value === value.value}
            options={employeeRegistryContractTypeValues || []}
            renderInput={(params) => (
              <TextField
                {...params}
                InputLabelProps={{ shrink: true }}
                label="Contract Type"
                variant="outlined"
              />
            )}
            value={getOptionFromValue(
              employeeRegistryContractTypeValues,
              employee.contracttype
            )}
            onChange={handleChange("contracttype")}
          />
        </Grid>

        <Grid item md={6} xs={12}>
          <Autocomplete
            disabled={isEmployeeRegistryValuesLoading || isSaving}
            getOptionLabel={(option) =>
              option.label ?? String(option.value ?? "")
            }
            getOptionSelected={(option, value) => option.value === value.value}
            options={employeeRegistryOfficeLocationValues || []}
            renderInput={(params) => (
              <TextField
                {...params}
                InputLabelProps={{ shrink: true }}
                label="Office Location"
                variant="outlined"
              />
            )}
            value={getOptionFromValue(
              employeeRegistryOfficeLocationValues,
              employee.officelocation
            )}
            onChange={handleChange("officelocation")}
          />
        </Grid>

        <Grid item md={6} xs={12}>
          <TextField
            fullWidth
            InputLabelProps={{ shrink: true }}
            disabled={isSaving}
            label="Office Type"
            value={employee.officetype || ""}
            variant="outlined"
            onChange={(e) =>
              setEmployee((prev) => ({ ...prev, officetype: e.target.value }))
            }
          />
        </Grid>

        <Grid item xs={12}>
          {(isEmployeeRegistryValuesLoading || isSaving) && (
            <CircularProgress size={20} />
          )}
        </Grid>

        <Grid item xs={12}>
          <Box display="flex" style={{ gap: 8 }}>
            <Button
              disabled={isSaving}
              variant="outlined"
              onClick={() => history.push("/welfares/employee-registry")}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              disabled={isSaving || isEmployeeRegistryValuesLoading}
              variant="contained"
              onClick={handleSave}
            >
              Save
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default EmployeeEditPage;
